import React, { useContext } from 'react';
import { Select, SelectField } from '@ubeya/shared-web/components';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useAddNewSelector from '@ubeya/shared-web/hooks/useAddNewSelector';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { hasRestrictedFeatures } from '@ubeya/shared/utils/accounts';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import GeneralLocationModal from 'settings/components/BranchSettings/Locations/GeneralLocationModal';

const LocationSelectField = React.forwardRef(
  ({ value, onChange, onAdd, name, title, customLocationsOptions, allowAdd = true, modalClassName, ...props }, ref) => {
    const { t, organizationId } = useContext(ConfigContext);
    const { accountId } = useAccount();

    const enableAddLocation = allowAdd && !hasRestrictedFeatures({ accountId, organizationId });

    const { locationsOptions: defaultLocationsOptions, mappedLocations, addLocation } = useLocations({
      branchId: props.branchId ?? null
    });

    const filteredLocationsOptions = defaultLocationsOptions.filter(({ isSubLocation }) => !isSubLocation);

    /* if there is pre selected value, and it deleted or isSubLocationDeleted, so the value
   in the select field won't be undefined */
    const locationIsDeleted =
      value &&
      !!mappedLocations[value] &&
      (mappedLocations[value].isSubLocation
        ? mappedLocations[value]?.isSubLocationDeleted
        : // we do not return is deleted for main location in mappedLocations
          !filteredLocationsOptions?.find(({ value: locationId }) => locationId === value));

    const selectedLocations = customLocationsOptions
      ? customLocationsOptions.filter(({ value: locationId, isSubLocation }) => {
          if (locationId === value && locationIsDeleted) {
            return false;
          }

          if (!isSubLocation) {
            // should be filteredLocationsOptions?.find(({ value: locationId }) => locationId === value));
            return true;
          }

          return !mappedLocations?.[locationId]?.isSubLocationDeleted;
        })
      : filteredLocationsOptions;

    const locationsOptionsWithDeleted = [
      ...selectedLocations,
      ...(locationIsDeleted
        ? [
            {
              value,
              label: `${mappedLocations[value]?.name} (${t('deleted')})`,
              isDisabled: true
            }
          ]
        : [])
    ];

    const {
      isOpen: locationModal,
      setIsOpen: setLocationModal,
      options: locationsOptions,
      onChange: onChangeLocation
    } = useAddNewSelector({
      options: locationsOptionsWithDeleted,
      addText: 'addNewLocation',
      onChange,
      enable: enableAddLocation
    });

    return (
      <>
        {onChange ? (
          <Select
            ref={ref}
            title={title || t('location')}
            value={value}
            onChange={onChangeLocation}
            options={locationsOptions}
            usePortal
            creatable={enableAddLocation}
            {...props}
          />
        ) : (
          <SelectField
            ref={ref}
            name={name ?? 'locationId'}
            title={title ?? t('locations')}
            onChange={onChangeLocation}
            options={locationsOptions}
            isMulti={false}
            usePortal
            creatable={enableAddLocation}
            {...props}
          />
        )}

        {locationModal && (
          <GeneralLocationModal
            className={modalClassName}
            selectedLocation={{ name: typeof locationModal === 'string' ? locationModal : '' }}
            onClose={() => setLocationModal(false)}
            onConfirm={async (values) => {
              const { data } = await addLocation(values);
              onChange ? onChangeLocation(data?.id, null, true, data) : onAdd(data?.id);
            }}
          />
        )}
      </>
    );
  }
);

export default LocationSelectField;
